import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import type { AdminWarning } from '@/types/admin-trace';

/**
 * Kanonische Warning-Regel (Spec 143 §5.3):
 * Alle Warnings der API werden gerendert — bekannte Codes mit i18n-Text,
 * unbekannte generisch (Code + Message). Einzige Ausnahme:
 * CLOUDWATCH_NOT_AVAILABLE wird unterdrueckt, solange der CW-Toggle
 * sichtbar ist (der Code dupliziert nur den Toggle, Trap §8.6).
 */
const KNOWN_WARNING_KEYS: Record<string, string> = {
  STATUS_HISTORY_INCOMPLETE: 'trace.warnings.statusHistoryIncomplete',
  CLOUDWATCH_QUERY_TIMEOUT: 'trace.warnings.cwQueryTimeout',
  CLOUDWATCH_LOGS_EXPIRED: 'trace.warnings.cwLogsExpired',
  CLOUDWATCH_PARTIAL_RESULTS: 'trace.warnings.cwPartialResults',
  CLOUDWATCH_QUERY_ERROR: 'trace.warnings.cwQueryError',
  CLOUDWATCH_LOGS_DELAYED: 'trace.warnings.cwLogsDelayed',
};

const SUPPRESSED_CODES = new Set(['CLOUDWATCH_NOT_AVAILABLE']);

export function TraceWarnings({ warnings }: { warnings: AdminWarning[] }) {
  const { t } = useTranslation('transactions');

  const visible = warnings.filter((w) => !SUPPRESSED_CODES.has(w.code));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="trace-warnings">
      {visible.map((w, idx) => {
        const knownKey = KNOWN_WARNING_KEYS[w.code];
        return (
          <div
            key={`${w.code}-${idx}`}
            className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="min-w-0">
              {knownKey ? (
                <p className="break-words">{t(knownKey)}</p>
              ) : (
                <p className="break-words">
                  <span className="break-all font-mono text-xs">{w.code}</span>
                  {w.message ? <> — {w.message}</> : null}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
